import classNames from 'clsx'
import { arrayOf, bool, node, number, oneOf, shape, string } from 'prop-types'
import { compose } from 'redux'

import './ProductItemWrapper.scss'
import {
  PRESCRIPTION_TYPE_ERX,
  PRESCRIPTION_TYPE_FREE,
  PRESCRIPTION_TYPE_INSURANCE,
  PRESCRIPTION_TYPE_PRIVAT,
  SUBSTITUTION_STATUS_VALUES,
} from 'shared/consts'
import { DOCUMENT_TYPE, ENTRY_TYPE } from 'shared/consts/checkout'
import {
  CART_FREE_GIFT_PROMOTION,
  EXPERIMENT_VARIATION,
} from 'shared/experiments/consts'
import { useGetExperiment } from 'shared/experiments/utils/experiments'
import { useGetExperimentNamePerTenant } from 'shared/experiments/utils/tenantBasedExperimentsUtils'
import { getPlaceholderImage } from 'shared/utils/imageUtils'
import { withI18n } from 'shared/utils/withI18n'
import dataMatrix from 'views/assets/images/misc/datamatrix-new.svg'
import Flag from 'views/components/atoms/Flag'
import IconOds from 'views/components/atoms/Icon/IconOds'
import LoadingIndicator from 'views/components/atoms/LoadingIndicator'
import ProductInfo from 'views/components/organisms/Cart/ProductInfo'
import ProductImage from 'views/components/organisms/ProductImage'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { isRedPointsActive } from 'views/utils/featureToggleUtils'
import {
  cartShippingDetail,
  i18nType,
  publicRuntimeConfigType,
} from 'views/utils/propTypes'

import ErxPriceLabelInfo from './ErxPriceLabelInfo'

const MAX_IMAGE_SIZE = 60

const getProductImageUri = ({ imageUrl, isERx, isPrescriptionItem }) => {
  if (isERx) {
    return typeof dataMatrix === 'string' ? dataMatrix : dataMatrix.src
  } else if (imageUrl) {
    return imageUrl
  }

  return getPlaceholderImage(isPrescriptionItem)
}

const ProductItemWrapper = ({
  rowNumber,
  children,
  productName,
  imageUrl,
  productId,
  available,
  productQuantity,
  isFreebie,
  outOfStockMessage,
  price,
  currency,
  unitPricing,
  subtitle,
  border,
  redPoints,
  rightColumnAlign,
  breakpoint,
  nowProductGroup,
  nowProduct,
  productType,
  additionalInformation,
  renderAvailability,
  isERx,
  prescriptionDate,
  prescriptionIssuedTo,
  priceLabelInfo,
  medicationType,
  displayCode,
  pzn,
  ean,
  isbn,
  upid,
  entryType,
  redPointsMultiplier,
  i18n,
  isDeleting,
  activeSubstance,
  substitutionStatus,
  originalPrescriptionPzn,
  originalPrescriptionProduct,
  volumePricing,
  sellerId,
  publicRuntimeConfig: {
    pageProperties: { featureToggles },
  },
}) => {
  const classNameOfferInfo = classNames(
    'l-flex l-flex--stretch-children l-flex__column',
    {
      'l-flex__start@medium': rightColumnAlign === 'top',
      'l-flex__row@medium': breakpoint === 'tablet',
      'l-flex__row@large': breakpoint === 'desktop',
    }
  )
  const productItemWrapperBorderClass = classNames({
    'u-border--bottom o-ProductItemWrapper-bordered': border,
  })

  const prescriptionDateOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }

  const isPrescriptionItem =
    productType === PRESCRIPTION_TYPE_ERX ||
    productType === PRESCRIPTION_TYPE_INSURANCE ||
    productType === PRESCRIPTION_TYPE_PRIVAT ||
    productType === PRESCRIPTION_TYPE_FREE

  const redPointsIsActive = isRedPointsActive(featureToggles)
  const cartFreeGiftPromotionExperiment = useGetExperiment(
    useGetExperimentNamePerTenant(CART_FREE_GIFT_PROMOTION)
  )
  const shouldShowFreebieBadge =
    cartFreeGiftPromotionExperiment?.isEnabled &&
    cartFreeGiftPromotionExperiment?.variant === EXPERIMENT_VARIATION.V1 &&
    isFreebie

  return (
    <div
      className={`o-ProductItemWrapper u-padding-large--ends ${productItemWrapperBorderClass} ${classNames(
        { 'greyed-out': isDeleting, 'greyed-out__background': true }
      )}`}
      data-qa-id={
        isERx ? `cart-entry-electronicPrescription` : `cart-entry-product`
      }
      data-medication-type={isERx ? medicationType : undefined}
    >
      {isDeleting && (
        <div className="pulse-wrapper">
          <LoadingIndicator size={60} thickness={3} />
        </div>
      )}
      <div
        data-pzn={`${productId}`}
        className="l-flex l-flex--start o-ProductItemWrapper__inner"
      >
        <div
          className="o-ProductItemWrapper-image u-margin-large--right"
          data-qa-id="cart-entry-entryImage"
        >
          <div className="o-ProductItemWrapper__image-wrapper">
            {productType === DOCUMENT_TYPE.ODS_SECTION &&
            entryType === ENTRY_TYPE.SERVICE ? (
              <div className="o-ProductItem--icon-background u-background-color--greyLight-light u-border-radius">
                <IconOds size="large" />
              </div>
            ) : (
              <ProductImage
                imageUrl={getProductImageUri({
                  imageUrl,
                  isERx,
                  isPrescriptionItem,
                })}
                productName={productName}
                alt={productName}
                isERX={isERx}
                name={productName}
                productImageBackgroundDark
                showBadges={false}
                shouldShowFreebieBadge={shouldShowFreebieBadge}
                srcHeight={MAX_IMAGE_SIZE}
                srcWidth={MAX_IMAGE_SIZE}
              />
            )}
          </div>
        </div>
        <div className="l-flex o-ProductItemWrapper--info l-flex--distribute-space@medium l-flex--stretch-children">
          <div className="u-margin--bottom u-no-margin--bottom@medium o-ProductItemWrapper--info-container">
            {productType === DOCUMENT_TYPE.ODS_SECTION &&
            entryType === ENTRY_TYPE.SERVICE ? (
              <div className="o-ProductInfo l-flex l-flex__column l-flex--start">
                <div className="u-font-weight--bold a-full-width o-ProductInfo__title">
                  {productName}
                </div>
                <TransWithHtml
                  text={i18n._('cart.product.ods.consultation.subtitle')}
                />
              </div>
            ) : (
              <ProductInfo
                rowNumber={rowNumber}
                productName={productName}
                unitPricing={unitPricing}
                productId={productId}
                available={available}
                outOfStockMessage={outOfStockMessage}
                subtitle={subtitle}
                currency={currency}
                price={price}
                productQuantity={productQuantity}
                redPoints={redPoints}
                nowProductGroup={nowProductGroup}
                nowProduct={nowProduct}
                productType={productType}
                additionalInformation={additionalInformation}
                renderAvailability={renderAvailability}
                prescriptionDate={
                  isERx && prescriptionDate
                    ? new Date(prescriptionDate).toLocaleDateString(
                        'de-DE',
                        prescriptionDateOptions
                      )
                    : undefined
                }
                prescriptionIssuedTo={prescriptionIssuedTo}
                isERx={isERx}
                medicationType={medicationType}
                displayCode={displayCode}
                pzn={pzn}
                ean={ean}
                isbn={isbn}
                upid={upid}
                activeSubstance={activeSubstance}
                substitutionStatus={substitutionStatus}
                originalPrescriptionPzn={originalPrescriptionPzn}
                originalPrescriptionProduct={originalPrescriptionProduct}
                volumePricing={volumePricing}
                sellerId={sellerId}
              />
            )}
            {redPointsMultiplier > 1 && redPointsIsActive && (
              <div
                className="u-margin-x-small--bottom u-margin-x-small--top"
                data-clientside-hook="redPointsMultiplier"
              >
                <Flag
                  label={`${redPointsMultiplier}x ${i18n._(
                    'general.label.product.redPoints'
                  )}`}
                  redPointsMultiplier
                />
              </div>
            )}
          </div>
          <div className={classNameOfferInfo}>{children}</div>
        </div>
      </div>
      {priceLabelInfo && (
        <div className="o-ProductItemWrapper__priceLabelInfoContainer u-margin-small--top">
          <ErxPriceLabelInfo priceLabelInfo={priceLabelInfo} />
        </div>
      )}
    </div>
  )
}

ProductItemWrapper.propTypes = {
  rowNumber: number,
  children: node,
  entryType: string,
  productName: string,
  unitPricing: string,
  imageUrl: string,
  productQuantity: number,
  isFreebie: bool,
  currency: string.isRequired,
  price: number.isRequired,
  productId: string,
  available: bool.isRequired,
  outOfStockMessage: string,
  subtitle: string,
  additionalInformation: string,
  renderAvailability: bool,
  border: bool,
  nowProductGroup: string,
  nowProduct: bool,
  rightColumnAlign: string,
  redPoints: number,
  isERx: bool,
  prescriptionDate: string,
  prescriptionIssuedTo: string,
  productType: string,
  medicationType: string,
  breakpoint: oneOf(['desktop', 'tablet']),
  displayCode: string,
  pzn: string,
  ean: string,
  isbn: string,
  upid: string,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  redPointsMultiplier: number,
  i18n: i18nType.isRequired,
  isDeleting: bool,
  activeSubstance: shape({ name: string, strength: number, unit: string }),
  substitutionStatus: oneOf(SUBSTITUTION_STATUS_VALUES),
  originalPrescriptionPzn: string,
  originalPrescriptionProduct: shape({
    name: string,
    unit: string,
    unitQuantityString: string,
  }),
  canonicalUrl: string,
  cartShippingDetail,
  shippingDetails: arrayOf({
    code: string,
    deliveryTime: { earliestDays: number, latestDays: number },
    label: string,
  }),
  isRx: bool,
  volumePricing: arrayOf({
    quantityThreshold: number,
    unitDiscountPrice: number | null,
    unitOriginPrice: number,
  }),
  priceLabelInfo: string,
  sellerId: string,
}

ProductItemWrapper.defaultProps = {
  children: null,
  productName: '',
  entryType: 'article',
  subtitle: null,
  additionalInformation: null,
  imageUrl: '',
  isFreebie: false,
  unitPricing: null,
  renderAvailability: true,
  redPoints: null,
  border: true,
  rightColumnAlign: 'center',
  productType: null,
  breakpoint: 'tablet',
  nowProductGroup: 'none',
  isERx: false,
  prescriptionDate: null,
  prescriptionIssuedTo: null,
  medicationType: null,
  productId: null,
  outOfStockMessage: '',
  displayCode: null,
  pzn: null,
  ean: null,
  isbn: null,
  upid: null,
  redPointsMultiplier: 1,
  isDeleting: false,
  activeSubstance: null,
  substitutionStatus: null,
  originalPrescriptionPzn: null,
  originalPrescriptionProduct: {},
  canonicalUrl: '',
  cartShippingDetail: undefined,
  shippingDetails: null,
  isRx: false,
  volumePricing: null,
  priceLabelInfo: null,
  sellerId: '',
}

export default compose(withI18n(), withShopAndRuntimeConfig)(ProductItemWrapper)
